/** Client-side cache of the Byteline write-up prepared in the background. */
import { supabase } from "@/integrations/supabase/client";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const cache = new Map<string, string | null>();
const inFlight = new Map<string, Promise<string | null>>();

export function getCachedPreparedStory(articleId?: string | null): string | null | undefined {
  if (!articleId) return undefined;
  return cache.get(articleId);
}

/** Reads the saved write-up for a story. Returns null when nothing is ready yet. */
export function loadPreparedStory(articleId?: string | null): Promise<string | null> {
  if (!articleId || !UUID_RE.test(articleId)) return Promise.resolve(null);
  if (cache.has(articleId)) return Promise.resolve(cache.get(articleId) ?? null);

  const existing = inFlight.get(articleId);
  if (existing) return existing;

  const request = (async () => {
    try {
      const { data } = await supabase
        .from("article_analysis")
        .select("analysis_data")
        .eq("article_id", articleId)
        .eq("analysis_type", "rewrite")
        .maybeSingle();
      const content = (data?.analysis_data as { content?: string } | null)?.content;
      const value = typeof content === "string" && content.length > 400 ? content : null;
      cache.set(articleId, value);
      return value;
    } catch {
      return null;
    } finally {
      inFlight.delete(articleId);
    }
  })();

  inFlight.set(articleId, request);
  return request;
}

/** Warms the cache for a story the reader is likely to open next. */
export function prefetchPreparedStory(articleId?: string | null) {
  if (!articleId || cache.has(articleId) || inFlight.has(articleId)) return;
  void loadPreparedStory(articleId);
}
